import React, { useCallback, useState } from 'react';
import { FocusTool, Icon, Tool } from '../basic';
import { Breakpoints, intl, useBreakpoint } from '../../lib';
import { AccessibleClickableContainer } from '../accessibility';
import { uniqueId } from 'lodash';

export type ListDetailItem = {
  id: string;
  title: string;
  link: () => JSX.Element;
  render: () => JSX.Element;
};

export type ListDetailProps = {
  title: string;
  items: ListDetailItem[];
  mobileItemPreview?: JSX.Element;
  unselectedState?: JSX.Element;
  onItemSelected?: (item: ListDetailItem) => void;
  initialSelectedItemId?: string;
  className?: string;
  contentAriaRole?: 'region' | 'form';
};

/**
 * A list-detail layout component that displays a list of items and their details.
 * @param title the title of the list shown on the top-left
 * @param items the items to be displayed in the list selection
 * @param mobileItemPreview an element that is shown as preview for link items on mobile
 * @param unselectedState a component to show when no item is selected
 * @param onItemSelected a callback function that is called when an item is selected
 * @param initialSelectedItemId the id of the item to be initially selected
 * @param contentAriaRole the ARIA role to apply to the content area (default is "region")
 * @param props additional props to pass to the root element
 * @constructor
 */
export function ListDetail({
  title,
  items,
  mobileItemPreview,
  unselectedState,
  onItemSelected,
  initialSelectedItemId,
  contentAriaRole = 'region',
  ...props
}: Readonly<ListDetailProps>): JSX.Element {
  const isMobileView = useBreakpoint(Breakpoints.MOBILE);
  const [selectedItem, setSelectedItem] = React.useState<
    ListDetailItem | undefined
  >(items.find(item => item.id === initialSelectedItemId));
  const [previewImageVisible, setPreviewImageVisible] = useState(true);
  const itemLinks = React.useMemo(() => {
    return items.map((item: ListDetailItem) => (
      <AccessibleClickableContainer
        key={'list-detail-item-' + item.id}
        className={
          'ehs-list-detail-item' +
          (selectedItem?.id === item.id ? ' selected' : '')
        }
        onClick={() => {
          setSelectedItem(item);
          if (onItemSelected) {
            onItemSelected(item);
          }
        }}
      >
        <div className="ehs-list-detail-item-link">{item.link()}</div>
        {selectedItem && selectedItem.id === item.id && (
          <FocusTool
            selector={() => {
              const selectors = [
                '.ehs-list-detail-right button',
                '.ehs-list-detail-right input',
                '.ehs-list-detail-right select',
                '.ehs-list-detail-right textarea'
              ];
              const query = selectors.join(', ');
              return document.querySelector(query) as HTMLElement;
            }}
            tooltip={intl(
              'patientaccessmanagementportlet.accessManagementPanel.button.showDetails'
            )}
          />
        )}
        <Icon iconName="arrow-right" aria-label={undefined} />
      </AccessibleClickableContainer>
    ));
  }, [items, onItemSelected, selectedItem]);

  const mobileItemPreviewRef = useCallback(
    element => {
      if (!element || !isMobileView) {
        setPreviewImageVisible(false);
        return;
      }

      const observer = new IntersectionObserver(
        ([entry]) => {
          setPreviewImageVisible(entry.isIntersecting);
        },
        { threshold: 0 }
      );

      observer.observe(element);

      return () => {
        observer.disconnect();
      };
    },
    [isMobileView]
  );

  const detailViewAriaDescriptionId = uniqueId('ehs-list-detail-');
  return (
    <div
      {...props}
      className={
        'ehs-list-detail' + (props.className ? ` ${props.className}` : '')
      }
    >
      {(!isMobileView || selectedItem === undefined) && items.length > 0 && (
        <div className="ehs-list-detail-left">
          {isMobileView && mobileItemPreview && (
            <div
              ref={mobileItemPreviewRef}
              className="ehs-list-detail-left-preview"
            >
              {mobileItemPreview}
            </div>
          )}
          <div className="ehs-list-detail-left-header-container">
            <div
              className={
                'ehs-list-detail-left-header' +
                (mobileItemPreview ? ' preview-available' : '') +
                (previewImageVisible ? ' preview-visible' : '')
              }
            >
              <h2 className="sh-font">{title}</h2>
            </div>
          </div>
          {itemLinks}
        </div>
      )}
      {(!isMobileView || selectedItem !== undefined || items.length === 0) && (
        <div
          className={
            'ehs-list-detail-right' + (selectedItem ? '' : ' empty-state')
          }
        >
          {selectedItem ? (
            <div
              className="ehs-list-detail-item-control-container"
              role={contentAriaRole}
              aria-labelledby={detailViewAriaDescriptionId}
            >
              <div className="ehs-list-detail-item-header">
                {isMobileView && (
                  <Tool
                    icon="arrow-left"
                    className="ehs-list-detail-item-header-spacer"
                    onClick={() => setSelectedItem(undefined)}
                    tooltip={intl('view.button.back')}
                  />
                )}
                <h3 id={detailViewAriaDescriptionId} className="sh-font">
                  {selectedItem.title}
                </h3>
                {isMobileView && (
                  <div className="ehs-list-detail-item-header-spacer"></div>
                )}
              </div>
              <div className="ehs-list-detail-item-control">
                {selectedItem.render()}
              </div>
            </div>
          ) : (
            unselectedState
          )}
        </div>
      )}
    </div>
  );
}
